import { getAnalyticsEvents } from './analyticsService';
import { getCollection } from './firestoreService';
import { convertTimestamp } from './firestoreService';
import { format, getHours, getDay, differenceInSeconds, differenceInMinutes } from 'date-fns';

export interface SessionMetrics {
  averageDuration: number;
  sessionsPerUser: {
    daily: number;
    weekly: number;
    monthly: number;
  };
  peakUsageTimes: {
    byHour: Array<{ hour: number; count: number }>;
    byDayOfWeek: Array<{ day: number; dayName: string; count: number }>;
  };
  sessionLengthDistribution: Array<{ range: string; count: number }>;
  bounceRate: number;
  avgTimeBetweenSessions: number;
  deepEngagementSessions: number;
  totalSessions: number;
}

export interface SessionActivityAnalytics {
  metrics: SessionMetrics;
  sessionsOverTime: Array<{ date: string; count: number }>;
  topSessions: Array<{ userId: string; duration: number; timestamp: Date }>;
}

export const getSessionActivityAnalytics = async (
  days: number = 30
): Promise<SessionActivityAnalytics> => {
  try {
    // Get all session-related events
    const events = await getAnalyticsEvents(days);
    
    // Filter session events
    const sessionStartEvents = events.filter(
      (e) => e.eventName === 'session_start' || e.eventName === 'app_launch'
    );
    
    // Group sessions by user and calculate durations
    const sessions: Array<{ userId: string; startTime: Date; endTime?: Date; duration?: number }> = [];
    const sessionMap = new Map<string, { startTime: Date; userId: string }>();
    
    // Process events chronologically
    const sortedEvents = [...sessionStartEvents].sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );
    
    sortedEvents.forEach((event) => {
      const sessionKey = `${event.userId}_${format(event.timestamp, 'yyyy-MM-dd')}`;
      const existing = sessionMap.get(sessionKey);
      
      if (!existing || differenceInMinutes(event.timestamp, existing.startTime) > 30) {
        // New session (gap > 30 min)
        if (existing) {
          // Close previous session
          sessions.push({
            userId: existing.userId,
            startTime: existing.startTime,
            endTime: event.timestamp,
            duration: differenceInMinutes(event.timestamp, existing.startTime),
          });
        }
        sessionMap.set(sessionKey, { startTime: event.timestamp, userId: event.userId });
      }
    });
    
    // Close remaining open sessions (use last event time as end)
    const lastEventTime = sortedEvents.length > 0 ? sortedEvents[sortedEvents.length - 1].timestamp : new Date();
    sessionMap.forEach((session) => {
      sessions.push({
        userId: session.userId,
        startTime: session.startTime,
        endTime: lastEventTime,
        duration: differenceInMinutes(lastEventTime, session.startTime),
      });
    });
    
    // Calculate metrics
    const totalSessions = sessions.length;
    const totalDuration = sessions.reduce((sum, s) => sum + (s.duration || 0), 0);
    const averageDuration = totalSessions > 0 ? totalDuration / totalSessions : 0;
    
    // Sessions per user
    const userSessionCounts = new Map<string, number>();
    sessions.forEach((s) => {
      userSessionCounts.set(s.userId, (userSessionCounts.get(s.userId) || 0) + 1);
    });
    
    const sessionsPerUserArray = Array.from(userSessionCounts.values());
    const sessionsPerUser = {
      daily: sessionsPerUserArray.length > 0 ? sessionsPerUserArray.reduce((a, b) => a + b, 0) / sessionsPerUserArray.length / days : 0,
      weekly: sessionsPerUserArray.length > 0 ? sessionsPerUserArray.reduce((a, b) => a + b, 0) / sessionsPerUserArray.length / (days / 7) : 0,
      monthly: sessionsPerUserArray.length > 0 ? sessionsPerUserArray.reduce((a, b) => a + b, 0) / sessionsPerUserArray.length / (days / 30) : 0,
    };
    
    // Peak usage times
    const hourlyCounts = new Map<number, number>();
    const dailyCounts = new Map<number, number>();
    
    sessions.forEach((s) => {
      const hour = getHours(s.startTime);
      hourlyCounts.set(hour, (hourlyCounts.get(hour) || 0) + 1);
      
      const dayOfWeek = getDay(s.startTime);
      dailyCounts.set(dayOfWeek, (dailyCounts.get(dayOfWeek) || 0) + 1);
    });
    
    const byHour = Array.from(hourlyCounts.entries())
      .map(([hour, count]) => ({ hour, count }))
      .sort((a, b) => a.hour - b.hour);
    
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const byDayOfWeek = Array.from(dailyCounts.entries())
      .map(([day, count]) => ({ day, dayName: dayNames[day], count }))
      .sort((a, b) => a.day - b.day);
    
    // Session length distribution
    const ranges = [
      { label: '< 1 min', min: 0, max: 1 },
      { label: '1-5 min', min: 1, max: 5 },
      { label: '5-15 min', min: 5, max: 15 },
      { label: '15-30 min', min: 15, max: 30 },
      { label: '30-60 min', min: 30, max: 60 },
      { label: '> 60 min', min: 60, max: Infinity },
    ];
    
    const distribution = ranges.map((range) => ({
      range: range.label,
      count: sessions.filter((s) => {
        const duration = s.duration || 0;
        return duration >= range.min && duration < range.max;
      }).length,
    }));
    
    // Bounce rate (sessions < 30 seconds)
    const bounceSessions = sessions.filter((s) => (s.duration || 0) < 0.5).length;
    const bounceRate = totalSessions > 0 ? (bounceSessions / totalSessions) * 100 : 0;
    
    // Deep engagement sessions (>30 min)
    const deepEngagementSessions = sessions.filter((s) => (s.duration || 0) > 30).length;
    
    // Time between sessions
    const userSessions = new Map<string, Date[]>();
    sessions.forEach((s) => {
      if (!userSessions.has(s.userId)) {
        userSessions.set(s.userId, []);
      }
      userSessions.get(s.userId)!.push(s.startTime);
    });
    
    let totalTimeBetween = 0;
    let timeBetweenCount = 0;
    
    userSessions.forEach((sessionDates) => {
      sessionDates.sort((a, b) => a.getTime() - b.getTime());
      for (let i = 1; i < sessionDates.length; i++) {
        const timeBetween = differenceInMinutes(sessionDates[i], sessionDates[i - 1]);
        totalTimeBetween += timeBetween;
        timeBetweenCount++;
      }
    });
    
    const avgTimeBetweenSessions = timeBetweenCount > 0 ? totalTimeBetween / timeBetweenCount : 0;
    
    // Sessions over time
    const sessionsByDate = new Map<string, number>();
    sessions.forEach((s) => {
      const dateKey = format(s.startTime, 'yyyy-MM-dd');
      sessionsByDate.set(dateKey, (sessionsByDate.get(dateKey) || 0) + 1);
    });
    
    const sessionsOverTime = Array.from(sessionsByDate.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
    
    // Top sessions by duration
    const topSessions = [...sessions]
      .sort((a, b) => (b.duration || 0) - (a.duration || 0))
      .slice(0, 20)
      .map((s) => ({
        userId: s.userId,
        duration: s.duration || 0,
        timestamp: s.startTime,
      }));
    
    const metrics: SessionMetrics = {
      averageDuration,
      sessionsPerUser,
      peakUsageTimes: {
        byHour,
        byDayOfWeek,
      },
      sessionLengthDistribution: distribution,
      bounceRate,
      avgTimeBetweenSessions,
      deepEngagementSessions,
      totalSessions,
    };
    
    return {
      metrics,
      sessionsOverTime,
      topSessions,
    };
  } catch (error) {
    console.error('Error calculating session analytics:', error);
    throw error;
  }
};

